import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CircularProgress, Box } from '@mui/material';
import { UserType } from '../data/types/auth';
import AccessDeniedPage from '../pages/AccessDeniedPage';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedUserTypes?: string[];
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowedUserTypes }) => {
  const { user, userType, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    // Redirect to login page but save the attempted url
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If allowedUserTypes is provided, check if the user's type is allowed
  if (allowedUserTypes && userType && !allowedUserTypes.includes(userType)) {
    // Redirect based on user type
    if (userType.toLowerCase() === UserType.USER) {
      return <Navigate to="/" replace />;
    } else if (userType.toLowerCase() === UserType.BUSINESS) {
      return <Navigate to="/dashboard" replace />;
    } else if (userType.toLowerCase() === UserType.SUPER_ADMIN) {
      return <Navigate to="/manage-business" replace />;
    }
    // Default to access denied page if user type doesn't match any known type
    return <AccessDeniedPage />;
  }

  return <>{children}</>;
}; 